import React from 'react';
import { Link } from 'react-router-dom';
import './WhoAreWe.css';
import ImpactStories from '../../components/ImpactStories';
import SEO from '../../components/SEO';
import heroImg from '../../assets/YOCILAB/IMG-20220909-WA0024.jpg';
import heroCoverImg from '../../assets/YOCILAB/IMG-20221006-WA0012 (1).jpg';
import missionImg from '../../assets/YOCILAB/IMG-20220909-WA0033.jpg';
import visionImg from '../../assets/YOCILAB/IMG-20220909-WA0028 (1).jpg';
import storyImg from '../../assets/YOCILAB/DSC_7431.jpg';

const coreValues = [
  {
    title: "Youth-Centered Innovation",
    desc: "We place young people at the center of solution design, empowering them with agency to build sustainable futures.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    )
  },
  {
    title: "Radical Inclusivity",
    desc: "Democratizing access to technology, AI education, and venture capital for talent in low-resource communities.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    )
  },
  {
    title: "Action & Real-World Proof",
    desc: "Moving beyond classroom theory into live venture creation, real digital products, and measurable community change.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    )
  },
  {
    title: "Collaborative Ecosystems",
    desc: "Uniting universities, industry leaders, global mentors, and grassroots communities into a unified support network.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    )
  }
];

const WhoAreWe = () => {
  return (
    <div className="who-are-we-page">
      <SEO
        title="Who Are We | YOCILAB"
        description="Learn about YOCILAB, a youth-led innovation ecosystem empowering young Africans to thrive through technology, mentorship, and seed funding."
      />

      {/* Hero Banner */}
      <section className="who-hero">
        <div className="who-hero__bg">
          <img src={heroCoverImg} alt="" className="who-hero__bg-img" />
          <div className="who-hero__overlay" />
        </div>
        <div className="container">
          <div className="who-hero__badge">
            <span className="who-hero__badge-dot" />
            DISCOVER YOCILAB
          </div>
          <h1 className="who-hero__title">
            Unlocking Africa's Boundless<br />
            <em>Creative & Economic Potential</em>
          </h1>
          <p className="who-hero__subtitle">
            YOCILAB is a youth-led innovation organization equipping young changemakers in underserved communities with future-ready skills, co-creation spaces, and seed capital to launch impactful ventures.
          </p>
        </div>
      </section>

      {/* Overview Card with Photo */}
      <section className="who-overview-section">
        <div className="container">
          <div className="who-overview-grid">
            <div className="who-overview-img-wrap">
              <img
                src={heroImg}
                alt="YOCILAB team and youth in action"
                className="who-overview-img"
              />
              <div className="who-overview-badge">
                <strong>Empowering Youth</strong>
                <span>From Idea to Impact</span>
              </div>
            </div>

            <div className="who-overview-text">
              <span className="section-pill">OUR ESSENCE</span>
              <h2>Ideas. Skills. Lasting Impact.</h2>
              <p className="who-lead-p">
                We equip young Africans with the technical tools, mentorship networks, and early-stage capital needed to build scalable solutions that generate livelihoods and strengthen local communities.
              </p>
              <p className="who-body-p">
                Operating across grassroots hubs, higher-education campuses, and digital platforms, YOCILAB bridges the gap between raw potential and market-ready execution in digital technology, green entrepreneurship, and creative intelligence.
              </p>
              <div className="who-actions">
                <Link to="/programs/yocilab-academy" className="btn btn-primary">
                  Explore Programs
                </Link>
                <Link to="/about/our-team" className="btn btn-secondary">
                  Meet Our Team
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Cards */}
      <section className="who-mv-section">
        <div className="container">
          <div className="who-mv-header">
            <span className="section-pill">PURPOSE & DIRECTION</span>
            <h2>Our Mission & Vision</h2>
          </div>

          <div className="who-mv-grid">
            <div className="who-mv-card">
              <div className="who-mv-img-wrap">
                <img src={missionImg} alt="Our Mission in Action" className="who-mv-img" />
                <span className="who-mv-tag">MISSION</span>
              </div>
              <div className="who-mv-body">
                <h3>To Equip & Empower</h3>
                <p>
                  To equip Africa's youth with in-demand technical capabilities, entrepreneurial mentorship, and seed resources to design, build, and scale solutions that create jobs and drive positive social change.
                </p>
              </div>
            </div>

            <div className="who-mv-card">
              <div className="who-mv-img-wrap">
                <img src={visionImg} alt="Our Vision for Africa" className="who-mv-img" />
                <span className="who-mv-tag who-mv-tag--accent">VISION</span>
              </div>
              <div className="who-mv-body">
                <h3>A Continent of Builders</h3>
                <p>
                  An Africa where every young person—regardless of background or geographic location—has the agency and opportunity to turn bold ideas into sustainable livelihoods and transformative societal impact.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="who-values-section">
        <div className="container">
          <div className="who-values-header">
            <span className="section-pill">WHAT GUIDES US</span>
            <h2>Our Core Values</h2>
            <p>The foundational principles that guide every program, partnership, and decision we make.</p>
          </div>

          <div className="who-values-grid">
            {coreValues.map((val, idx) => (
              <div key={idx} className="who-value-card">
                <div className="who-value-icon-wrap">
                  {val.icon}
                </div>
                <h3>{val.title}</h3>
                <p>{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Genesis & Founder Story */}
      <section className="who-story-section">
        <div className="container">
          <div className="who-story-card">
            <div className="who-story-img-wrap">
              <img src={storyImg} alt="Founder Birndze Bete Dzekewong" className="who-story-img" />
            </div>
            <div className="who-story-body">
              <span className="section-pill">THE GENESIS</span>
              <h2>Our Story</h2>
              <p>
                YOCILAB was founded on the unshakeable belief that young people in underserved communities possess the ingenuity and passion to solve Africa's most urgent challenges.
              </p>
              <p>
                Our founder, <strong>Birndze Bete Dzekewong</strong>, recognized that talented young builders should not have to leave their hometowns or countries to access world-class mentorship, modern technology, and early-stage capital. 
              </p>
              <p>
                Since inception, YOCILAB has grown from a grassroots vision into a multi-program ecosystem supporting thousands of learners through hands-on bootcamps, the ABETA AI platform, co-creation labs, and annual Youth Summits.
              </p>
            </div>
          </div>
        </div>
      </section>

      <ImpactStories />
    </div>
  );
};

export default WhoAreWe;